import {
  calculateAtomEconomy,
  checkAtomEconomyAnswer,
  type AtomEconomyResult,
} from './chemistryCalculations';
import { reviewed, type DataSource, type ReviewStatus } from './energyCalorimetry';
import { formatSig, type AnswerCheck } from './gasLaws';
import { SYNTHESIS_SOURCES } from './synthesisDesign';

/** The twelve principles of green chemistry (Anastas and Warner, 1998). */
export type GreenPrinciple =
  | 'prevention'
  | 'atom-economy'
  | 'less-hazardous-synthesis'
  | 'safer-chemicals'
  | 'safer-solvents'
  | 'energy-efficiency'
  | 'renewable-feedstocks'
  | 'reduce-derivatives'
  | 'catalysis'
  | 'design-for-degradation'
  | 'real-time-analysis'
  | 'accident-prevention';

export const GREEN_PRINCIPLES: { id: GreenPrinciple; number: number; label: string; meaning: string }[] = [
  { id: 'prevention', number: 1, label: 'Prevent waste', meaning: 'Avoid making waste rather than treating it later.' },
  { id: 'atom-economy', number: 2, label: 'Maximise atom economy', meaning: 'Put as many reactant atoms as possible into the product.' },
  { id: 'less-hazardous-synthesis', number: 3, label: 'Less hazardous syntheses', meaning: 'Use and make substances with little toxicity.' },
  { id: 'safer-chemicals', number: 4, label: 'Design safer chemicals', meaning: 'Products should do their job with minimal toxicity.' },
  { id: 'safer-solvents', number: 5, label: 'Safer solvents and auxiliaries', meaning: 'Avoid or minimise solvents and separation agents.' },
  { id: 'energy-efficiency', number: 6, label: 'Design for energy efficiency', meaning: 'Run reactions near room temperature and pressure when possible.' },
  { id: 'renewable-feedstocks', number: 7, label: 'Use renewable feedstocks', meaning: 'Start from renewable rather than depleting raw materials.' },
  { id: 'reduce-derivatives', number: 8, label: 'Reduce derivatives', meaning: 'Avoid temporary blocking or protecting steps.' },
  { id: 'catalysis', number: 9, label: 'Use catalysts', meaning: 'Catalysts are better than reagents used up in stoichiometric amounts.' },
  { id: 'design-for-degradation', number: 10, label: 'Design for degradation', meaning: 'Products should break down harmlessly after use.' },
  { id: 'real-time-analysis', number: 11, label: 'Real-time analysis', meaning: 'Monitor processes to prevent pollution as it happens.' },
  { id: 'accident-prevention', number: 12, label: 'Inherently safer chemistry', meaning: 'Choose substances and forms that reduce the risk of accidents.' },
];

export type RouteKey = 'A' | 'B';

export interface SynthesisRoute {
  name: string;
  /** Balanced ASCII equation read by the shared equation parser. */
  equation: string;
  desiredFormula: string;
  /** Reagents, catalysts or conditions shown beside the route. */
  detail: string;
}

export interface GreenComparison {
  id: string;
  product: string;
  routes: Record<RouteKey, SynthesisRoute>;
  /** Route whose atom economy the learner calculates (always one that is below 100%). */
  calculate: RouteKey;
  /** The route judged greener overall in this comparison. */
  preferred: RouteKey;
  principleQuestion: string;
  acceptedPrinciples: GreenPrinciple[];
  tradeOff: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function comparison(entry: Omit<GreenComparison, 'sources' | 'reviewStatus'>): GreenComparison {
  return {
    ...entry,
    ...reviewed([SYNTHESIS_SOURCES.industrial, SYNTHESIS_SOURCES.anastas, SYNTHESIS_SOURCES.trost, SYNTHESIS_SOURCES.iupac]),
  };
}

export const GREEN_COMPARISONS: GreenComparison[] = [
  comparison({
    id: 'ethanol-hydration-fermentation',
    product: 'Ethanol',
    routes: {
      A: { name: 'Hydration of ethene', equation: 'C2H4 + H2O -> C2H5OH', desiredFormula: 'C2H5OH', detail: 'H₃PO₄ catalyst, about 300 °C, 6–7 MPa; ethene from crude oil or natural gas' },
      B: { name: 'Fermentation of glucose', equation: 'C6H12O6 -> 2C2H5OH + 2CO2', desiredFormula: 'C2H5OH', detail: 'Yeast, about 30–40 °C; glucose from sugar cane or grain' },
    },
    calculate: 'B',
    preferred: 'B',
    principleQuestion: 'Fermentation has the lower atom economy. Which principle can still favour it?',
    acceptedPrinciples: ['renewable-feedstocks'],
    tradeOff: 'Hydration wastes no atoms but depends on a fossil feedstock. Fermentation loses mass as CO₂ and needs energy for distillation, but starts from a crop that regrows.',
  }),
  comparison({
    id: 'ethanol-hydration-substitution',
    product: 'Ethanol',
    routes: {
      A: { name: 'Hydration of ethene', equation: 'C2H4 + H2O -> C2H5OH', desiredFormula: 'C2H5OH', detail: 'Addition reaction with an acid catalyst' },
      B: { name: 'Substitution of chloroethane', equation: 'C2H5Cl + NaOH -> C2H5OH + NaCl', desiredFormula: 'C2H5OH', detail: 'Aqueous NaOH, heated under reflux' },
    },
    calculate: 'B',
    preferred: 'A',
    principleQuestion: 'Which principle best explains why hydration is the greener route?',
    acceptedPrinciples: ['atom-economy', 'prevention'],
    tradeOff: 'Substitution makes one NaCl for every ethanol, and the chloroethane itself had to be made first.',
  }),
  comparison({
    id: 'ethanoic-acid-carbonylation',
    product: 'Ethanoic acid',
    routes: {
      A: { name: 'Methanol carbonylation', equation: 'CH3OH + CO -> CH3COOH', desiredFormula: 'CH3COOH', detail: 'Rhodium or iridium catalyst (Monsanto and Cativa processes)' },
      B: { name: 'Oxidation of ethanol', equation: 'C2H5OH + O2 -> CH3COOH + H2O', desiredFormula: 'CH3COOH', detail: 'Bacterial or catalytic oxidation' },
    },
    calculate: 'B',
    preferred: 'A',
    principleQuestion: 'Which principles explain why carbonylation dominates industrial production?',
    acceptedPrinciples: ['atom-economy', 'catalysis'],
    tradeOff: 'Carbonylation puts every atom into the product using a small amount of catalyst. Ethanol oxidation loses water, but bioethanol makes it a renewable route, which is how vinegar is made.',
  }),
  comparison({
    id: 'chloroethane-addition-substitution',
    product: 'Chloroethane',
    routes: {
      A: { name: 'Addition of HCl to ethene', equation: 'C2H4 + HCl -> C2H5Cl', desiredFormula: 'C2H5Cl', detail: 'Addition across the C=C bond' },
      B: { name: 'Chlorination of ethane', equation: 'C2H6 + Cl2 -> C2H5Cl + HCl', desiredFormula: 'C2H5Cl', detail: 'UV light; free-radical substitution' },
    },
    calculate: 'B',
    preferred: 'A',
    principleQuestion: 'Which principle best explains why addition is preferred?',
    acceptedPrinciples: ['atom-economy', 'prevention'],
    tradeOff: 'Substitution also makes dichloroethanes and other over-chlorinated by-products, so its real yield is lower than its atom economy suggests.',
  }),
  comparison({
    id: 'ethylene-oxide',
    product: 'Epoxyethane (ethylene oxide)',
    routes: {
      A: { name: 'Direct catalytic oxidation', equation: '2C2H4 + O2 -> 2C2H4O', desiredFormula: 'C2H4O', detail: 'Silver catalyst, about 250 °C' },
      B: { name: 'Chlorohydrin route (overall)', equation: 'C2H4 + Cl2 + Ca(OH)2 -> C2H4O + CaCl2 + H2O', desiredFormula: 'C2H4O', detail: 'Chlorine and slaked lime, two stages' },
    },
    calculate: 'B',
    preferred: 'A',
    principleQuestion: 'Which principles favour the direct oxidation route?',
    acceptedPrinciples: ['atom-economy', 'prevention', 'less-hazardous-synthesis', 'catalysis'],
    tradeOff: 'The chlorohydrin route used toxic chlorine and, by its equation, makes 111 g of CaCl₂ waste for every 44 g of product. Direct oxidation still burns some ethene to CO₂ in practice.',
  }),
  comparison({
    id: 'maleic-anhydride',
    product: 'Maleic anhydride',
    routes: {
      A: { name: 'Oxidation of benzene', equation: '2C6H6 + 9O2 -> 2C4H2O3 + 4CO2 + 4H2O', desiredFormula: 'C4H2O3', detail: 'Vanadium oxide catalyst' },
      B: { name: 'Oxidation of butane', equation: '2C4H10 + 7O2 -> 2C4H2O3 + 8H2O', desiredFormula: 'C4H2O3', detail: 'Vanadium phosphate catalyst' },
    },
    calculate: 'A',
    preferred: 'B',
    principleQuestion: 'Besides atom economy, which principle favours butane over benzene?',
    acceptedPrinciples: ['less-hazardous-synthesis', 'prevention'],
    tradeOff: 'Benzene is a known carcinogen and two of its six carbon atoms leave as CO₂. Butane avoids both problems.',
  }),
  comparison({
    id: 'ethyl-ethanoate',
    product: 'Ethyl ethanoate',
    routes: {
      A: { name: 'Fischer esterification', equation: 'CH3COOH + C2H5OH -> CH3COOC2H5 + H2O', desiredFormula: 'CH3COOC2H5', detail: 'Concentrated H₂SO₄ catalyst; an equilibrium' },
      B: { name: 'Tishchenko reaction', equation: '2CH3CHO -> CH3COOC2H5', desiredFormula: 'CH3COOC2H5', detail: 'Aluminium alkoxide catalyst' },
    },
    calculate: 'A',
    preferred: 'B',
    principleQuestion: 'Which principles favour the Tishchenko route?',
    acceptedPrinciples: ['atom-economy', 'catalysis'],
    tradeOff: 'Esterification loses water and stops at equilibrium. The Tishchenko reaction joins two ethanal molecules with no by-product.',
  }),
  comparison({
    id: 'methanol-co-co2',
    product: 'Methanol',
    routes: {
      A: { name: 'From carbon monoxide', equation: 'CO + 2H2 -> CH3OH', desiredFormula: 'CH3OH', detail: 'Cu/ZnO catalyst; synthesis gas usually from natural gas' },
      B: { name: 'From captured carbon dioxide', equation: 'CO2 + 3H2 -> CH3OH + H2O', desiredFormula: 'CH3OH', detail: 'Cu/ZnO catalyst; CO₂ captured from a waste stream, H₂ from electrolysis' },
    },
    calculate: 'B',
    preferred: 'B',
    principleQuestion: 'The CO₂ route has the lower atom economy. Which principle can favour it?',
    acceptedPrinciples: ['renewable-feedstocks', 'prevention'],
    tradeOff: 'Using waste CO₂ as a feedstock costs one extra H₂ per methanol, which leaves as water. It is only greener if the hydrogen is made with low-emission electricity.',
  }),
  comparison({
    id: 'hydrogen-electrolysis-reforming',
    product: 'Hydrogen',
    routes: {
      A: { name: 'Steam reforming and shift (overall)', equation: 'CH4 + 2H2O -> CO2 + 4H2', desiredFormula: 'H2', detail: 'Nickel catalyst, about 700–1000 °C, then the water–gas shift' },
      B: { name: 'Electrolysis of water', equation: '2H2O -> 2H2 + O2', desiredFormula: 'H2', detail: 'Electricity from solar or wind' },
    },
    calculate: 'B',
    preferred: 'B',
    principleQuestion: 'Electrolysis has the lower atom economy. Which principle can favour it?',
    acceptedPrinciples: ['renewable-feedstocks'],
    tradeOff: 'Hydrogen is so light that both routes have low atom economy. The real difference is the fossil CH₄ feedstock and the CO₂ released by reforming.',
  }),
];

export type RouteChoice = RouteKey | 'equal';

export interface RouteEconomy {
  key: RouteKey;
  route: SynthesisRoute;
  result: AtomEconomyResult;
}

/** Atom economy of both routes, or null when either equation fails the shared checks. */
export function routeEconomies(entry: GreenComparison): Record<RouteKey, RouteEconomy> | null {
  const a = calculateAtomEconomy({ equation: entry.routes.A.equation, desiredFormula: entry.routes.A.desiredFormula });
  const b = calculateAtomEconomy({ equation: entry.routes.B.equation, desiredFormula: entry.routes.B.desiredFormula });
  if (!a.value || !b.value) return null;
  return {
    A: { key: 'A', route: entry.routes.A, result: a.value },
    B: { key: 'B', route: entry.routes.B, result: b.value },
  };
}

/** Atom economies within this many percentage points are treated as equal. */
const EQUAL_ECONOMY_POINTS = 0.5;

export function higherEconomy(entry: GreenComparison): RouteChoice | null {
  const economies = routeEconomies(entry);
  if (!economies) return null;
  const difference = economies.A.result.percent - economies.B.result.percent;
  if (Math.abs(difference) < EQUAL_ECONOMY_POINTS) return 'equal';
  return difference > 0 ? 'A' : 'B';
}

export interface GreenPredictionCheck {
  correct: boolean;
  feedback: string;
}

export function checkHigherEconomy(entry: GreenComparison, choice: RouteChoice): GreenPredictionCheck | null {
  const economies = routeEconomies(entry);
  const answer = higherEconomy(entry);
  if (!economies || !answer) return null;
  const a = `${formatSig(economies.A.result.percent, 3)}%`;
  const b = `${formatSig(economies.B.result.percent, 3)}%`;
  const summary = `Route A: ${a}; route B: ${b}.`;
  if (choice === answer) {
    return { correct: true, feedback: `Correct. ${summary} The route with fewer or lighter by-products wastes less reactant mass.` };
  }
  const winner = answer === 'equal' ? 'neither route' : `route ${answer}`;
  return {
    correct: false,
    feedback: `${summary} So ${winner} has the higher atom economy. Look at what else is on the product side: every by-product is reactant mass that does not end up in the ${entry.product.toLowerCase()}.`,
  };
}

export function checkRouteEconomy(entry: GreenComparison, answer: number): AnswerCheck {
  const route = entry.routes[entry.calculate];
  return checkAtomEconomyAnswer({ equation: route.equation, desiredFormula: route.desiredFormula }, answer);
}

export function checkPrinciple(entry: GreenComparison, principle: GreenPrinciple): GreenPredictionCheck {
  const chosen = GREEN_PRINCIPLES.find((item) => item.id === principle);
  if (!chosen) return { correct: false, feedback: 'Choose one of the twelve principles.' };
  const accepted = entry.acceptedPrinciples
    .map((id) => GREEN_PRINCIPLES.find((item) => item.id === id)?.label)
    .filter(Boolean)
    .join('; ');
  if (entry.acceptedPrinciples.includes(principle)) {
    return { correct: true, feedback: `Yes: principle ${chosen.number}, ${chosen.label.toLowerCase()}. ${entry.tradeOff}` };
  }
  return {
    correct: false,
    feedback: `Principle ${chosen.number} (${chosen.label.toLowerCase()}) is not the main argument here. Reviewed answer: ${accepted}. ${entry.tradeOff}`,
  };
}
